import { Functionality, Language, Pointer } from '@yleisradio/areena-types';
import { idFromPointer } from '../utils/pointer';
import {
  CREDENTIALS_LOGIN_SUB_PATH,
  EPISODES_PATH,
  INFO_PATH,
  ITEMS_PATH,
  LOGIN_PATH,
  PAIR_LOGIN_SUB_PATH,
  PLAYER_PATH,
  REGISTER_LOGIN_SUB_PATH,
  SETTINGS_PATH,
  SIMILAR_PATH,
  PROFILES_PATH,
  TEST_PLAYER_PATH,
  TESTS_PATH,
  PACKAGES_PATH,
  LIVE_PATH,
  SEARCH_PATH,
  DEVICE_INFO_PATH,
  FAVORITES_PATH,
  RADIO_FRONT_PATH,
  NOT_FOUND_PATH,
  HOME_PATH,
} from './routeNames';
import { LoginSubRoute } from '../appTypings';
import { To } from 'react-router-dom';
import { views } from '../utils/view';
import { logger } from '../../common/logger';

type Params = {
  pointer?: Pointer | undefined;
  functionality?: Functionality | undefined;
  languageCode?: Language;
};

/**
 * @param pointer
 * @param functionality
 */
const getRoutePath = ({ pointer, functionality }: Params): string | To => {
  if (pointer?.uri) {
    const pathFromPointer = getRoutePathByPointer(pointer);

    if (pathFromPointer) return pathFromPointer;

    const regex = /ui\/(.*?)\.json/;

    const matches = pointer.uri.match(regex);
    if (matches && matches.length > 1 && matches[1]) {
      return `/${matches[1]}`;
    }
    const cardPointerRegex = /yleareena:\/\/(.*?)$/;
    const cardMatches = pointer.uri.match(cardPointerRegex);

    if (cardMatches && cardMatches.length > 1 && cardMatches[1]) {
      return `/${cardMatches[1]}`;
    }
  }

  if (functionality) {
    return getRoutePathByFunctionality(functionality);
  }

  logger.warn(
    `Could not parse routepath from pointer or functionality: ${JSON.stringify({ pointer, functionality })}`
  );

  return `/${NOT_FOUND_PATH}`;
};

const PLAYER_VIEW = '/player.json';
const LIVE_VIEW = '/live.json';
const LIVE_BUFFER = '/player/';

type LiveBufferParameters = {
  programId?: string;
  publicationId?: string;
};

const isSubProfileLanging = (pointerUri: string) => {
  const ageGroupFrontPageRegex = /.*\d-front.*/;
  return pointerUri.match(ageGroupFrontPageRegex);
};

const isSubProfilePodcasts = (pointerUri: string) => {
  return /\d-podcasts/.test(pointerUri);
};

const getSubProfilePodcastsPathByPointerUri = (
  pointerUri: string
): string | undefined => {
  const subProfilePodcasts = /views\/(.*?)\.json/.exec(pointerUri);
  return subProfilePodcasts && subProfilePodcasts[1]
    ? `/${PACKAGES_PATH}/${subProfilePodcasts[1]}`
    : '/';
};

const getLiveBufferParametersByPointerUri = (
  pointerUri: string
): LiveBufferParameters => {
  const liveParams = /player\/([0-9-]+)\/([0-9-]+)\.json/.exec(pointerUri);
  return liveParams && liveParams[1] && liveParams[2]
    ? { programId: liveParams[1], publicationId: liveParams[2] }
    : {};
};

export const programIdFromParameter = (uri: string) =>
  uri.match(/programId=([0-9-]+)/);

export const isPackageViewPointer = (itemId: string): boolean => {
  const idPattern = /^(?:30|57)-/;
  return idPattern.test(itemId);
};

function getRoutePathByViewPointerUri(
  pointerUri: string,
  itemId: string
): string | To | undefined {
  const LEGACY_LASTEN_FAVORITES_URI = 'tv-omat'; // lasten favorites page until API podcast lasten menu task is ready

  if (pointerUri.includes('episodes') && itemId.length) {
    return routePathsById.episodes(itemId);
  } else if (pointerUri.includes('similar') && itemId.length) {
    return routePathsById.similar(itemId);
  } else if (
    [PLAYER_VIEW, LIVE_VIEW, LIVE_BUFFER].some(uriPiece =>
      pointerUri.includes(uriPiece)
    ) &&
    itemId.length
  ) {
    const liveBufferParameters =
      getLiveBufferParametersByPointerUri(pointerUri);

    const itemIdFromParameter = programIdFromParameter(pointerUri)?.[1];
    const search = Object.entries(liveBufferParameters)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');

    return {
      pathname: routePathsById.player(itemIdFromParameter ?? itemId),
      search,
    };
  } else if (isSubProfilePodcasts(pointerUri)) {
    return getSubProfilePodcastsPathByPointerUri(pointerUri);
  } else if (pointerUri.includes('radio-front')) {
    return `/${RADIO_FRONT_PATH}`;
  } else if (pointerUri.includes('tv-front')) {
    return `/${HOME_PATH}`;
  } else if (isSubProfileLanging(pointerUri)) {
    return `/${HOME_PATH}`;
  } else if (isPackageViewPointer(itemId)) {
    return routePathsById.package(itemId);
  } else if (pointerUri.includes('live')) {
    return `/${LIVE_PATH}`;
  } else if (
    pointerUri.includes(LEGACY_LASTEN_FAVORITES_URI) ||
    pointerUri.includes(views.FAVORITES)
  ) {
    return `/${FAVORITES_PATH}`;
  }
}

function getRoutePathByPointer(
  pointer: Pointer & { uri: string }
): string | To | undefined {
  const itemId = idFromPointer(pointer);

  switch (pointer.type) {
    case 'clip':
    case 'program':
    case 'series':
      return routePathsById.item(itemId);
    case 'service':
      return routePathsById.player(itemId);
    case 'view':
      return getRoutePathByViewPointerUri(pointer.uri, itemId);
  }
}

const loginSubRouteFunctionalityMap: {
  [subRouteFromFunctionality: string]: LoginSubRoute;
} = {
  liita: PAIR_LOGIN_SUB_PATH,
  tunnuksella: CREDENTIALS_LOGIN_SUB_PATH,
  luotunnus: REGISTER_LOGIN_SUB_PATH,
};

function getRoutePathByFunctionality(functionality: Functionality) {
  switch (functionality.id) {
    case 'profile':
      return `/${PROFILES_PATH}`;
    case 'search':
      return `/${SEARCH_PATH}`;
    case 'settings':
      return `/${SETTINGS_PATH}`;
    case 'device-page':
      return `/${DEVICE_INFO_PATH}`;
    case 'login':
      return `/${LOGIN_PATH}`;
  }

  if (functionality.id.includes('login')) {
    const subRouteString = functionality.id.split('/')[1];
    const subRoute = subRouteString?.length
      ? loginSubRouteFunctionalityMap[subRouteString]
      : undefined;

    return getLoginRoutePath(subRoute);
  }

  return `/${functionality.id}`;
}

export const routePathsById = {
  episodes: (itemId: string) =>
    `${routePathsById.item(itemId)}/${EPISODES_PATH}`,
  info: (itemId: string) => `${routePathsById.item(itemId)}/${INFO_PATH}`,
  item: (itemId: string) => `/${ITEMS_PATH}/${itemId}`,
  similar: (itemId: string) => `${routePathsById.item(itemId)}/${SIMILAR_PATH}`,
  player: (itemId: string) => `${routePathsById.item(itemId)}/${PLAYER_PATH}`,
  editProfile: (userId: string, fieldName?: string) =>
    `/${PROFILES_PATH}/${userId}/edit/${fieldName || ''}`,
  testPlayer: (testId: number | string) =>
    `/${TESTS_PATH}/${testId}/${TEST_PLAYER_PATH}`,
  package: (packageId: string) => `/${PACKAGES_PATH}/${packageId}`,
};

export const getLoginRoutePath = (loginSubRoute?: string) =>
  loginSubRoute?.length
    ? `/${LOGIN_PATH}?t=${loginSubRoute}`
    : `/${LOGIN_PATH}`;

export default getRoutePath;
